import { z } from "zod";
import type { Operation } from "../types";

// Gates — the productized ship/verify checks, surfaced in the Workflows tab of
// /agents (docs/agents/AGENTS-PRD.md slice 1). Slice 1 sources "the workspace's
// projects" from the Local Apps registry (excellent_apps + excellent_app_installs
// — the existing model of "a local dev project this workspace knows about, with
// a path on this device"). The read path (list) reads @excellent/core/gates
// directly server-side; only the mutating "Run gates" action needs a real route.

const gateDefSchema = z.object({
  id: z.string(),
  label: z.string(),
  command: z.string(),
  enabled: z.boolean(),
  source: z.enum(["detected", "starter", "manual"]),
});
export type GateDefWire = z.infer<typeof gateDefSchema>;

const gateResultSchema = z.object({
  id: z.string(),
  label: z.string(),
  command: z.string(),
  ok: z.boolean(),
  code: z.number().nullable(),
  ms: z.number(),
  output: z.string(),
});
export type GateResultWire = z.infer<typeof gateResultSchema>;

const gatesRunSummarySchema = z.object({
  root: z.string(),
  ranAt: z.string(),
  ok: z.boolean(),
  total: z.number(),
  passed: z.number(),
  failed: z.number(),
  durationMs: z.number(),
  results: z.array(gateResultSchema),
});
export type GatesRunSummaryWire = z.infer<typeof gatesRunSummarySchema>;

const projectGatesSchema = z.object({
  appId: z.number(),
  name: z.string(),
  slug: z.string(),
  root: z.string(),
  hasConfig: z.boolean(),
  gates: z.array(gateDefSchema),
  lastRun: gatesRunSummarySchema.nullable(),
});
export type ProjectGatesWire = z.infer<typeof projectGatesSchema>;

const listResponse = z.object({ projects: z.array(projectGatesSchema) });
type ListResponse = z.infer<typeof listResponse>;

const runBody = z.object({
  appId: z.number().int().positive(),
  /** Omit for the retained project-page "run all" action. Companion Home names one check. */
  gateId: z.string().min(1).optional(),
  /** Optional durable linkage emitted by the companion's reviewed-workflow path. */
  workflowId: z.string().min(1).optional(),
  observationReviewId: z.string().min(1).optional(),
  acceptedContractDigest: z.string().min(1).optional(),
});
export type GatesRunBody = z.infer<typeof runBody>;

/**
 * The verdict a durable gate result record can carry — the same five values the
 * verification protocol declares (`packages/core/src/verification/protocol/identifiers.ts`,
 * ranked once in `verdict-law.ts`). Named and exported so a call site can switch over it
 * exhaustively: a reader that keys on one literal and lets the rest fall through is what
 * rendered a MISMATCH as "0 passing and 0 failing".
 */
export const gateRecordStatusSchema = z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]);
export type GateRecordStatus = z.infer<typeof gateRecordStatusSchema>;

const runResponse = z.object({
  project: projectGatesSchema,
  /** Present only for a named check linked to a validated reviewed workflow or accepted contract. */
  resultRecord: z
    .object({
      id: z.number().int().positive(),
      href: z.string().min(1),
      status: gateRecordStatusSchema.optional(),
      acceptedContractDigest: z.string().min(1).optional(),
      attemptId: z.string().min(1).optional(),
      evidenceGraphDigest: z.string().min(1).optional(),
      receiptId: z.string().min(1).optional(),
      receiptPayloadDigest: z.string().min(1).optional(),
    })
    .nullable(),
});
type RunResponse = z.infer<typeof runResponse>;

const listOp: Operation<void, void, void, ListResponse> = {
  method: "GET",
  path: () => "/api/gates",
  response: listResponse,
};

const runOp: Operation<void, void, GatesRunBody, RunResponse> = {
  method: "POST",
  path: () => "/api/gates/run",
  body: runBody,
  response: runResponse,
};

export const gates = {
  list: listOp,
  run: runOp,
};
